/* eslint-disable @typescript-eslint/ban-types */
import React, { MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Box, Typography, TypographyProps } from '@mui/material';

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';

import EditButton from '@ui-elements/EditButton';
import SaveButton from '@ui-elements/SaveButton';
import ModalWithInput from '@ui-parts/ModalWithInput';

import { ButtonProps } from '@material-ui/core/Button';
import { PayloadAction } from '@reduxjs/toolkit';
import { useAppDispatch } from '~/App/hooks';
import { PALETTE, BASE_FONT_FAMILY } from '~/assets/styles/base';
import { mixinCircle } from '~/assets/styles/mixin';
import { SURVEY_FORM_TYPE } from '~/common/constants';
import { ROUTES } from '~/constants/routes';
import { getPreviewFinalize, getMiniEnqueteRelations } from '~/apiTs';

import { PreviewSurveyResponse } from '~/common/types/api/users';
import { MiniEnqueteRelationsResponse } from '~/common/types/api/admin';

import { GMOASK_STATUS, ORIGINS } from '~/constants/api';

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  min-height: 200px;
  border: 1px solid;
  border-color: ${PALETTE.border.frame};
  background: ${PALETTE.white};
  outline: none;
  word-break: break-all;
  & > :not(:first-child) {
    flex: 1 1 auto;
  }
`;

const DataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 21px 0;
  & > :last-child {
    flex: 1 1 auto;
  }
`;

type StyledBoxProps = {
  $index: number;
  $surveyType: number;
};

const StyledBox = styled.div<StyledBoxProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding: 8px 0 20px;
  ${(props) =>
    props.$index % 2 === 1 &&
    css`
      border-top: 1px solid #EAF1F5;
    };
    `}
`;

const Content = styled.div`
  padding-right: 55px;
`;

const Title = styled.div`
  margin-bottom: 5px;
  font-weight: bold;
`;

const Body = styled(Typography)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  line-height: 24px;
`;

type ButtonContainerProps = {
  $top?: string;
};
const ButtonContainer = styled.div<ButtonContainerProps>`
  display: flex;
  gap: 10px;
`;

const StyledButton = styled.button`
  ${mixinCircle}
  transition: opacity 0.4s;
  color: #a2b2be;
  &:hover {
    opacity: 0.7;
  }
`;

type InputProps = {
  rules?: {};
  label?: string;
  nameProp: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  width?: string;
  handleChange?: ButtonProps['onClick'];
  handleBlur?: ButtonProps['onClick'];
  onSubmit?: ButtonProps['onClick'];
  position?: 'center' | 'left' | 'right';
  textAlign?: 'center' | 'left' | 'right';
  errorWidth?: string;
  errorZIndex?: number;
  dataTestId?: string;
  onlyNum?: boolean;
  inputProps?: {};
  multiline?: boolean;
  rows?: number;
};

type ModalProps = {
  open: boolean;
  closeModal: MouseEventHandler<HTMLButtonElement>;
  openModal: Function;
  titleProps: {
    titleLabelVariant?: TypographyProps['variant'];
    titleVariant?: TypographyProps['variant'];
    descVariant?: TypographyProps['variant'];
    titleLabel?: string;
    title?: string;
    desc?: string;
  };
  inputs: Array<InputProps>;
  handleClickSave: Function;
  disabledButton?: boolean;
  dataTestId: string;
  control: {};
};

type Props = {
  dataset: Array<{
    name: string;
    title: string;
    label?: string;
    modal?: ModalProps;
  }>;
  canEdit?: boolean;
  surveyType: number;
  surveyId: number;
  isAdmin?: boolean;
};

const SurveyBasicInfoBox: React.FC<Props & ButtonProps> = ({
  dataset,
  canEdit = false,
  surveyType,
  surveyId,
  isAdmin = false,
}): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const showPreviewPage = async () => {
    const { payload: previewContent } = (await dispatch(
      getPreviewFinalize({
        surveyId,
      })
    )) as unknown as PayloadAction<
      PreviewSurveyResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;

    if (previewContent.status === GMOASK_STATUS.OK) {
      const { redirectUrl } = previewContent.result;
      window.open(redirectUrl, '_blank')?.focus();
    }
  };

  const showSetttingCheckLink = async () => {
    const { payload: miniEnqueteRelations } = (await dispatch(
      getMiniEnqueteRelations({
        surveyId,
      })
    )) as unknown as PayloadAction<
      MiniEnqueteRelationsResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;

    if (miniEnqueteRelations.status === GMOASK_STATUS.OK) {
      const { miniEnqueteId } = miniEnqueteRelations.result;
      const url = `${ORIGINS.SIMPLE_SURVEY}/mini_enquetes/view/${miniEnqueteId}`;
      window.open(url, '_blank')?.focus();
    }
  };

  return (
    <Wrapper data-testid="uiParts-SurveyBasicInfoBox">
      {surveyType === SURVEY_FORM_TYPE.ORIGINAL_FORM && (
        <Box
          data-testid="original-form-label-box"
          sx={{
            fontSize: '12px',
            width: 'fit-content',
            padding: '6px 16px',
            borderRight: `1px solid ${PALETTE.border.frame}`,
            borderBottom: `1px solid ${PALETTE.border.frame}`,
            background: PALETTE.blue.light,
            color: PALETTE.text.main,
            fontFamily: BASE_FONT_FAMILY,
          }}
        >
          {t('survey.originalFormLabel')}
        </Box>
      )}
      {surveyType === SURVEY_FORM_TYPE.GOOGLE_FORM && (
        <Box
          data-testid="google-form-label-box"
          sx={{
            fontSize: '12px',
            width: 'fit-content',
            padding: '4px 16px',
            borderRight: `1px solid ${PALETTE.border.frame}`,
            borderBottom: `1px solid ${PALETTE.border.frame}`,
            background: PALETTE.blue.light,
            color: PALETTE.text.main,
            fontFamily: BASE_FONT_FAMILY,
          }}
        >
          {t('survey.googleFormLabel')}
        </Box>
      )}
      <DataWrapper>
        {dataset.map((data, i) => (
          <StyledBox $index={i} key={i} $surveyType={surveyType}>
            <Content>
              <Title>
                <Typography variant="title3">{data.title}</Typography>
              </Title>
              {data.label && <Body variant="bodyDefault">{data.label}</Body>}
            </Content>
            {data.name === 'surveyUrl' && surveyType === SURVEY_FORM_TYPE.ORIGINAL_FORM && (
              <ButtonContainer>
                {canEdit && !isAdmin && (
                  <EditButton
                    handleClick={() => {
                      navigate(
                        ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM_MANUAL.replace(
                          ':id',
                          surveyId.toString()
                        )
                      );
                    }}
                    name={data.name}
                  >
                    <EditRoundedIcon />
                  </EditButton>
                )}
                <StyledButton data-testid="preview-button" onClick={showPreviewPage}>
                  <RemoveRedEyeRoundedIcon />
                </StyledButton>
                {isAdmin && (
                  <StyledButton data-testid="setting-button" onClick={showSetttingCheckLink}>
                    <SettingsRoundedIcon />
                  </StyledButton>
                )}
              </ButtonContainer>
            )}
            {data.modal && canEdit && (
              <>
                <ButtonContainer>
                  <EditButton
                    handleClick={() => {
                      data?.modal?.openModal(data.name);
                    }}
                    name={data.name}
                  >
                    <EditRoundedIcon />
                  </EditButton>
                </ButtonContainer>

                <ModalWithInput
                  open={data.modal.open}
                  handleClose={data.modal.closeModal}
                  inputs={data.modal.inputs}
                  titleProps={data.modal.titleProps}
                  width="601px"
                  buttons={[
                    <SaveButton
                      key="button1"
                      handleClick={() => {
                        data?.modal?.handleClickSave();
                      }}
                      disabled={data.modal.disabledButton}
                      sx={{ width: 148 }}
                    />,
                  ]}
                  dataTestId={data.modal.dataTestId}
                  control={data.modal.control}
                />
              </>
            )}
          </StyledBox>
        ))}
      </DataWrapper>
    </Wrapper>
  );
};

export default SurveyBasicInfoBox;
